import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Lock, Tag, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    address: '',
    city: '',
    zipCode: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    if (!formData.email) {
      toast.error('Please enter your email first');
      return;
    }

    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API_URL}/validate-coupon`, {
        code: couponCode.toUpperCase(),
        customer_email: formData.email
      });

      if (response.data.valid) {
        setAppliedCoupon({
          code: couponCode.toUpperCase(),
          discountPercentage: response.data.discount_percentage
        });
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const calculateBulkDiscount = () => {
    const quantity = cart.reduce((sum, item) => sum + item.quantity, 0);
    // This should ideally call the backend endpoint for bulk discount calculation
    // For now, using simple logic
    if (quantity >= 5) return 20;
    if (quantity >= 4) return 15;
    if (quantity >= 3) return 10;
    if (quantity >= 2) return 5;
    return 0;
  };

  const calculateTotal = () => {
    const subtotal = getCartTotal();
    const bulkDiscount = calculateBulkDiscount();
    const couponDiscount = appliedCoupon?.discountPercentage || 0;
    
    // Apply bulk discount first
    const afterBulkDiscount = subtotal * (1 - bulkDiscount / 100);
    
    // Then apply coupon discount (stackable)
    const finalTotal = afterBulkDiscount * (1 - couponDiscount / 100);
    
    return {
      subtotal,
      bulkDiscount,
      couponDiscount,
      bulkDiscountAmount: subtotal * (bulkDiscount / 100),
      couponDiscountAmount: afterBulkDiscount * (couponDiscount / 100),
      total: finalTotal
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    const totals = calculateTotal();

    // If 100% discount, create subscriber directly without payment
    if (totals.total === 0 || (appliedCoupon?.discountPercentage === 100)) {
      try {
        // Mark coupon as used
        if (appliedCoupon) {
          await axios.post(`${API_URL}/use-coupon`, {
            code: appliedCoupon.code,
            customer_email: formData.email
          });
        }

        // Create subscriber with 1 year access
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

        await axios.post(`${API_URL}/subscribers`, {
          email: formData.email,
          name: formData.name,
          subscription_type: 'all_books',
          amount_paid: 0,
          marketing_consent: true
        });

        toast.success('🎉 Free subscription activated! You now have 1 year access to all books!');
        clearCart();
        setIsProcessing(false);
        navigate('/my-books');
        return;
      } catch (error) {
        toast.error('Failed to activate subscription. Please contact support.');
        setIsProcessing(false);
        return;
      }
    }

    // Normal payment processing (mock for now)
    setTimeout(async () => {
      try {
        // Mark coupon as used if applied
        if (appliedCoupon) {
          await axios.post(`${API_URL}/use-coupon`, {
            code: appliedCoupon.code,
            customer_email: formData.email
          });
        }

        toast.success('Order placed successfully! 🎉');
        clearCart();
        setIsProcessing(false);
        navigate('/order-confirmation');
      } catch (error) {
        toast.error('Failed to complete order');
        setIsProcessing(false);
      }
    }, 2000);
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Add some books to your cart before checking out.</p>
            <Button onClick={() => navigate('/books')}>Browse Books</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-800 mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Order Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Shipping Address */}
              <Card>
                <CardHeader>
                  <CardTitle>Shipping Address</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      name="address"
                      required
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Main St"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="New York"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode">ZIP Code</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        required
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        placeholder="10001"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Information - Hide if 100% discount */}
              {calculateTotal().total > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CreditCard className="mr-2 h-5 w-5" />
                      Payment Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-yellow-800">
                        <Lock className="inline h-4 w-4 mr-1" />
                        This is a demo checkout. Use any test card number (e.g., 4242 4242 4242 4242)
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="cardNumber">Card Number</Label>
                      <Input
                        id="cardNumber"
                        name="cardNumber"
                        required={calculateTotal().total > 0}
                        value={formData.cardNumber}
                        onChange={handleInputChange}
                        placeholder="4242 4242 4242 4242"
                        maxLength="19"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="cardExpiry">Expiry Date</Label>
                        <Input
                          id="cardExpiry"
                          name="cardExpiry"
                          required={calculateTotal().total > 0}
                          value={formData.cardExpiry}
                          onChange={handleInputChange}
                          placeholder="MM/YY"
                          maxLength="5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cardCvc">CVC</Label>
                        <Input
                          id="cardCvc"
                          name="cardCvc"
                          required={calculateTotal().total > 0}
                          value={formData.cardCvc}
                          onChange={handleInputChange}
                          placeholder="123"
                          maxLength="3"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Free Access Notice */}
              {calculateTotal().total === 0 && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-6 text-center">
                    <h3 className="text-2xl font-bold text-green-800 mb-2">
                      🎉 Free Access Granted!
                    </h3>
                    <p className="text-green-700 mb-4">
                      Your coupon gives you 1 year of free access to all books.
                      No payment required - just click below to activate!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Coupon Code */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Tag className="mr-2 h-5 w-5 text-purple-600" />
                    Coupon Code
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {appliedCoupon ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-green-800">
                            Coupon Applied: {appliedCoupon.code}
                          </p>
                          <p className="text-sm text-green-700">
                            {appliedCoupon.discountPercentage}% discount
                            {appliedCoupon.discountPercentage === 100 && ' - FREE ACCESS!'}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={removeCoupon}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="uppercase"
                      />
                      <Button
                        type="button"
                        onClick={validateCoupon}
                        disabled={validatingCoupon}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {validatingCoupon ? 'Validating...' : 'Apply'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg"
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 
                  calculateTotal().total === 0 ? 
                    'Activate Free Subscription' : 
                    `Pay $${calculateTotal().total.toFixed(2)}`}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div key={item.id} className="flex gap-3">
                      <img
                        src={item.coverImage}
                        alt={item.title}
                        className="w-16 h-20 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm line-clamp-2">{item.title}</p>
                        <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                        <p className="text-sm font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>${calculateTotal().subtotal.toFixed(2)}</span>
                    </div>
                    {calculateTotal().bulkDiscount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Bulk Discount ({calculateTotal().bulkDiscount}%)</span>
                        <span>-${calculateTotal().bulkDiscountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {appliedCoupon && (
                      <div className="flex justify-between text-sm text-purple-600 font-semibold">
                        <span>Coupon ({appliedCoupon.code} - {appliedCoupon.discountPercentage}%)</span>
                        <span>-${calculateTotal().couponDiscountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>Shipping</span>
                      <span className="text-green-600">Free</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total</span>
                      <span className={`${calculateTotal().total === 0 ? 'text-green-600' : 'text-blue-600'}`}>
                        ${calculateTotal().total.toFixed(2)}
                        {calculateTotal().total === 0 && ' (FREE!)'}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;